/**
 * Safety Test Models
 *
 * Interfaces for safety/knowledge tests, categories, and test attempts.
 */

// ============================================
// SAFETY TEST STATUS
// ============================================

export type SafetyTestStatus = 'not_started' | 'in_progress' | 'passed' | 'failed' | 'expired' | 'new_version_available';

// ============================================
// SAFETY TEST CATEGORY
// ============================================

export interface SafetyTestCategory {
  id: string;
  name: string;
  shortName: string;
  description?: string;
  icon: string;
  isRequired: boolean;
  frequency: 'annual' | 'biannual' | 'once' | 'custom';
  frequencyDays?: number;
  sortOrder: number;
  organizationId?: string; // null for system-wide categories
}

// ============================================
// SAFETY TEST INTERFACES
// ============================================

export interface SafetyTest {
  id: string;
  categoryId?: string;
  category?: SafetyTestCategory;
  title: string;
  shortName?: string;
  description?: string;
  icon?: string;
  status: SafetyTestStatus;
  lastAttemptDate?: string;
  lastScore?: number;
  passingScore: number;
  expirationDate?: string;
  requiredAnnually?: boolean;
  isCustom?: boolean;
  questionCount?: number;
  timeLimit?: number; // in minutes
  createdDate?: string;
  studyGuideUrl?: string;
  version?: number;
  shuffleQuestions?: boolean;
  shuffleOptions?: boolean;
}

// ============================================
// TEST QUESTION INTERFACES
// ============================================

export interface TestQuestion {
  id: string;
  text: string;
  options: TestQuestionOption[];
  correctOptionId?: string; // Only visible in results, not during test-taking
  explanation?: string;
  type?: 'multiple_choice' | 'true_false';
}

export interface TestQuestionOption {
  id: string;
  text: string;
  isCorrect?: boolean; // Only visible in results
}

// ============================================
// TEST ATTEMPT INTERFACES
// ============================================

export interface TestAttempt {
  id: string;
  testId: string;
  test?: SafetyTest;
  profileId: string;
  organizationId: string;
  startedAt: string;
  completedAt?: string;
  status: 'in_progress' | 'completed' | 'timed_out' | 'abandoned';
  score?: number;
  passed?: boolean;
  correctAnswers?: number;
  totalQuestions: number;
  currentQuestionIndex: number;
  questionIds: string[];
  timeLimitExpiresAt?: string;
  timeSpentSeconds?: number;
  expiresAt?: string; // Certification expiration
  answers?: TestAnswer[];
}

export interface TestAnswer {
  questionId: string;
  selectedOptionId: string;
  isCorrect?: boolean;
  answeredAt?: string;
}

// ============================================
// TEST RESULT SUMMARY
// ============================================

export interface TestResultSummary {
  totalQuestions: number;
  correctAnswers: number;
  incorrectAnswers: number;
  score: number;
  passed: boolean;
  passingScore: number;
  timeSpent: string;
  completedAt?: string;
  expiresAt?: string;
}

// ============================================
// API RESPONSE TYPES
// ============================================

export interface TestWithStatus extends SafetyTest {
  userStatus: SafetyTestStatus;
  lastAttempt?: TestAttempt;
  questionCount: number;
}

export interface StartAttemptResponse {
  attempt: TestAttempt;
  questions: TestQuestion[];
  resumed: boolean;
}

export interface SubmitAttemptResponse {
  score: number;
  passed: boolean;
  passingScore: number;
  totalQuestions: number;
  correctAnswers: number;
  incorrectAnswers: number;
  timeSpent: string;
  expiresAt?: string;
}
